import { FC, useCallback, useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  StackLayout,
  ParagraphText,
  StatusBanner,
} from '@leagueplatform/genesis-core';
import { Address, ContentProps } from 'models/profile-settings.model';
import { useAddressForm } from 'hooks/use-address-form.hook';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';
import { AddressEditSection } from './address-edit-section.component';
import { DetailSection } from '../detail-section.component';
import { ReadOnlyDetailsSection } from '../read-only-details-section.component';

interface AddressInformationProps {
  homeAddress: Address | undefined;
  mailingAddress: Address | undefined;
  onSave?: (home: Address, mailing: Address) => void;
  disableUnsavedModal?: boolean;
}

export const AddressInformationComponent: FC<AddressInformationProps> = ({
  homeAddress,
  mailingAddress,
  onSave,
  disableUnsavedModal = false,
}) => {
  const { formatMessage } = useIntl();
  const {
    addresses,
    editValues,
    getFieldError,
    handleFieldChange,
    hasAnyAddressChanges,
    transformAddress,
    canSave,
  } = useAddressForm(mailingAddress || {}, homeAddress || {});
  const { isAddressEditable } = useProductSegmentation();

  const formatValue = useCallback(
    (value: string) => {
      if (!value || value.trim() === '') {
        return formatMessage({ id: 'STR_NOT_PROVIDED' });
      }
      return value;
    },
    [formatMessage],
  );

  const contents: ContentProps[] = useMemo(
    () =>
      addresses.map((addressContent) => {
        const address =
          addressContent.type === 'mailing' ? mailingAddress : homeAddress;

        // Check if address has any data
        const hasAddressData =
          address &&
          (address.address1 || address.city || address.state || address.postal);

        return {
          labelId: addressContent.title,
          value: hasAddressData
            ? `${address.address1 || ''}, ${address.city || ''}, ${
                address.state || ''
              } ${address.postal || ''}`
            : ' ', // Use a space instead of empty string so formatValue gets called
          formatValue,
        };
      }),
    [addresses, mailingAddress, homeAddress, formatValue],
  );

  const handleSave = useCallback(
    (values: Record<string, string>) => {
      if (!onSave) return;

      const homeAddressData = transformAddress(
        values,
        homeAddress || {},
        'home',
      );
      const mailingAddressData = transformAddress(
        values,
        mailingAddress || {},
        'mailing',
      );

      onSave(homeAddressData, mailingAddressData);
    },
    [onSave, transformAddress, homeAddress, mailingAddress],
  );

  const renderEditContent = ({
    onChange: detailSectionOnChange,
    isMobile,
  }: {
    editValues: Record<string, string>;
    onChange: (id: string, value: string) => void;
    isMobile: boolean;
  }) => {
    // Bridge function that updates both DetailSection and address form state
    const handleAddressChange = (id: string, value: string) => {
      // Update DetailSection's editValues
      detailSectionOnChange(id, value);
      // Update address form state (for error handling and validation)
      handleFieldChange(id, value);
    };

    return (
      <StackLayout
        spacing="$threeQuarters"
        verticalAlignment="stretch"
        horizontalAlignment="stretch"
        css={{ width: '100%', marginY: '$quarter' }}
      >
        <StatusBanner
          status="info"
          css={{ backgroundColor: '$highlightBackgroundSubdued' }}
        >
          <ParagraphText css={{ fontSize: '$bodyTwo' }}>
            {formatMessage({ id: 'STR_ADDRESS_INFORMATION_LABEL' })}
          </ParagraphText>
        </StatusBanner>

        {addresses.map((address) => (
          <AddressEditSection
            key={address.type}
            address={address}
            editValues={editValues} // Use address form's editValues for validation
            onChange={handleAddressChange}
            isMobile={isMobile}
            getFieldError={getFieldError}
            hasAnyAddressChanges={hasAnyAddressChanges}
          />
        ))}
      </StackLayout>
    );
  };

  return isAddressEditable ? (
    <DetailSection
      title="STR_ADDRESS_INFORMATION"
      description="STR_ADDRESS_INFORMATION_LABEL"
      contents={contents}
      renderEditContent={renderEditContent}
      onSave={handleSave}
      canSave={hasAnyAddressChanges ? canSave : true}
      disableUnsavedModal={disableUnsavedModal}
      formatValue={formatValue}
    />
  ) : (
    <ReadOnlyDetailsSection
      title="STR_ADDRESS_INFORMATION"
      description="STR_PROFILE_SETTINGS_ADDRESS_DESCRIPTION"
    />
  );
};
